import { useQuery, useQueries } from '@tanstack/react-query';
import { getAggregatedData } from '../historical-data';
import { AggregatedDataParams, AggregatedDataResponse } from '../historical-data/types';
import { useMemo } from 'react';

interface MapDataQueryParams {
  siteId: string;
  startDate: string;
  endDate: string;
  enabled?: boolean;
}

interface MapAggregatedData {
  energy: {
    solarEnergy: number;
    gridEnergy: number;
    totalEnergy: number;
  };
  occupancy: {
    inCount: number;
    outCount: number;
    totalOccupancy: number;
  };
  temperature: {
    avgIndoor: number;
    avgOutdoor: number;
  };
}

// Helper function to count unique days from the response
function countUniqueDays(data: AggregatedDataResponse | undefined): number {
  if (!data?.data?.data || !Array.isArray(data.data.data)) {
    return 0;
  }
  
  const uniqueDates = new Set<string>();
  
  data.data.data.forEach(item => {
    if (item.values && Array.isArray(item.values)) {
      item.values.forEach(v => {
        if (v.timestamp) {
          // Extract just the date part (YYYY-MM-DD)
          const dateOnly = v.timestamp.split('T')[0];
          uniqueDates.add(dateOnly);
        }
      });
    }
  });
  
  return uniqueDates.size > 0 ? uniqueDates.size : 1;
}

// Helper function to aggregate values from the response
function aggregateValues(data: AggregatedDataResponse | undefined, datapoint: string): number {
  // Check if data.data.data exists and is an array
  if (!data?.data?.data || !Array.isArray(data.data.data)) {
    return 0;
  }
  
  const result = data.data.data.find(item => item.datapoint === datapoint);
  if (!result?.values || !Array.isArray(result.values)) {
    return 0;
  }
  
  const sum = result.values.reduce((sum, item) => sum + (item.value || 0), 0);
  return sum;
}

// Helper function to average values from the response
function averageValues(data: AggregatedDataResponse | undefined, datapoint: string): number {
  if (!data?.data?.data) return 0;
  
  const result = data.data.data.find(item => item.datapoint === datapoint);
  if (!result?.values || result.values.length === 0) return 0;
  
  const sum = result.values.reduce((total, item) => total + item.value, 0);
  return sum / result.values.length;
}

export function useMapAggregatedData({ siteId, startDate, endDate, enabled = true }: MapDataQueryParams) {
  // Define all queries
  const queries = [
    // Energy data query
    {
      queryKey: ['map-aggregated-energy', siteId, startDate, endDate],
      queryFn: () => getAggregatedData({
        table_name: 'aggregated_data_1day',
        site_id: siteId,
        device_id: 'solar_subsystem',
        datapoints: ['today_grid_energy', 'today_energy'],
        start_timestamp: startDate,
        end_timestamp: endDate,
        tz: 'Asia/Bangkok'
      }),
      enabled,
      staleTime: 300000, // 5 minutes
    },
    // Occupancy data query
    {
      queryKey: ['map-aggregated-occupancy', siteId, startDate, endDate],
      queryFn: () => getAggregatedData({
        table_name: 'aggregated_data_1day',
        site_id: siteId,
        device_id: 'cctv_subsystem',
        datapoints: ['in_count_daily', 'out_count_daily'],
        start_timestamp: startDate,
        end_timestamp: endDate,
        tz: 'Asia/Bangkok'
      }),
      enabled,
      staleTime: 300000,
    },
    // Outdoor temperature query
    {
      queryKey: ['map-aggregated-outdoor-temp', siteId, startDate, endDate],
      queryFn: () => getAggregatedData({
        table_name: 'aggregated_data_1day',
        site_id: siteId,
        device_id: siteId, // Use site_id for device_id as per spec
        datapoints: ['outdoor_temperature'],
        start_timestamp: startDate,
        end_timestamp: endDate,
        tz: 'Asia/Bangkok'
      }),
      enabled,
      staleTime: 300000,
    },
    // Indoor temperature query
    {
      queryKey: ['map-aggregated-indoor-temp', siteId, startDate, endDate],
      queryFn: () => getAggregatedData({
        table_name: 'aggregated_data_1day',
        site_id: siteId,
        device_id: 'zone_main',
        datapoints: ['avg_room_temperature'],
        start_timestamp: startDate,
        end_timestamp: endDate,
        tz: 'Asia/Bangkok'
      }),
      enabled,
      staleTime: 300000,
    }
  ];

  const results = useQueries({ queries });

  // Destructure results
  const [energyQuery, occupancyQuery, outdoorTempQuery, indoorTempQuery] = results;

  // Calculate aggregated data
  const aggregatedData = useMemo((): MapAggregatedData => {
    // Energy calculations
    const gridEnergy = aggregateValues(energyQuery.data, 'today_grid_energy');
    const solarEnergy = aggregateValues(energyQuery.data, 'today_energy');
    const totalEnergy = gridEnergy + solarEnergy;

    // Occupancy calculations
    const inCount = aggregateValues(occupancyQuery.data, 'in_count_daily');
    const outCount = aggregateValues(occupancyQuery.data, 'out_count_daily');
    const totalOccupancy = inCount - outCount;

    // Temperature calculations
    const avgOutdoor = averageValues(outdoorTempQuery.data, 'outdoor_temperature');
    const avgIndoor = averageValues(indoorTempQuery.data, 'avg_room_temperature');

    return {
      energy: {
        solarEnergy,
        gridEnergy,
        totalEnergy
      },
      occupancy: {
        inCount,
        outCount,
        totalOccupancy
      },
      temperature: {
        avgIndoor,
        avgOutdoor
      }
    };
  }, [energyQuery.data, occupancyQuery.data, outdoorTempQuery.data, indoorTempQuery.data]);

  // Combine loading and error states
  const isLoading = results.some(query => query.isLoading);
  const isError = results.some(query => query.isError);
  const errors = results.filter(query => query.error).map(query => query.error);

  return {
    data: aggregatedData,
    isLoading,
    isError,
    errors,
    refetch: () => Promise.all(results.map(query => query.refetch())),
  };
}

// Hook for fetching multiple sites data
export function useMultiSiteMapData(sites: { siteId: string; area?: number }[], startDate: string, endDate: string, enabled = true) {
  const results = useQueries({
    queries: sites.map(site => ({
      queryKey: ['multi-site-map-data', site.siteId, startDate, endDate],
      queryFn: async () => {
        
        try {
          const data = await Promise.all([
            // Energy data
            getAggregatedData({
              table_name: 'aggregated_data_1day',
              site_id: site.siteId,
              device_id: 'solar_subsystem',
              datapoints: ['today_grid_energy', 'today_energy'],
              start_timestamp: startDate,
              end_timestamp: endDate,
              tz: 'Asia/Bangkok'
            }),
            // Occupancy data
            getAggregatedData({
              table_name: 'aggregated_data_1day',
              site_id: site.siteId,
              device_id: 'cctv_subsystem',
              datapoints: ['in_count_daily', 'out_count_daily'],
              start_timestamp: startDate,
              end_timestamp: endDate,
              tz: 'Asia/Bangkok'
            }),
            // Temperature data
            Promise.all([
              getAggregatedData({
                table_name: 'aggregated_data_1day',
                site_id: site.siteId,
                device_id: site.siteId,
                datapoints: ['outdoor_temperature'],
                start_timestamp: startDate,
                end_timestamp: endDate,
                tz: 'Asia/Bangkok'
              }),
              getAggregatedData({
                table_name: 'aggregated_data_1day',
                site_id: site.siteId,
                device_id: 'zone_main',
                datapoints: ['avg_room_temperature'],
                start_timestamp: startDate,
                end_timestamp: endDate,
                tz: 'Asia/Bangkok'
              })
            ])
          ]);

          
          const [energyData, occupancyData, [outdoorTempData, indoorTempData]] = data;

        // Process the data
        const gridEnergy = aggregateValues(energyData, 'today_grid_energy');
        const solarEnergy = aggregateValues(energyData, 'today_energy');
        const totalEnergy = gridEnergy + solarEnergy;

        const inCount = aggregateValues(occupancyData, 'in_count_daily');
        const outCount = aggregateValues(occupancyData, 'out_count_daily');
        const totalOccupancy = inCount - outCount;

        const avgOutdoor = averageValues(outdoorTempData, 'outdoor_temperature');
        const avgIndoor = averageValues(indoorTempData, 'avg_room_temperature');

        // Get actual day count from the energy data (most reliable for daily data)
        const actualDayCount = countUniqueDays(energyData);

        // Calculate derived metrics according to calculation logic
        const eui = site.area ? totalEnergy / site.area : 0;
        const energySavingSaved = 5000; // Mock as per spec
        const totalEnergySavingSaved = energySavingSaved + solarEnergy;
        const carbonEmissionSaved = totalEnergySavingSaved * 0.4999;
        const equivalentTreesPlanted = carbonEmissionSaved / 33.5;

        return {
          siteId: site.siteId,
          energy: {
            solarEnergy,
            gridEnergy,
            totalEnergy,
            eui,
            energySavingSaved,
            totalEnergySavingSaved,
            carbonEmissionSaved,
            equivalentTreesPlanted,
            actualDayCount  // Include actual day count from data
          },
          occupancy: {
            inCount,
            outCount,
            totalOccupancy
          },
          temperature: {
            avgIndoor,
            avgOutdoor
          }
        };
        } catch (error) {
          console.error(`Error processing data for site ${site.siteId}:`, error);
          // Return null or default values on error
          return null;
        }
      },
      enabled,
      staleTime: 300000,
    }))
  });

  const isLoading = results.some(query => query.isLoading);
  const isError = results.some(query => query.isError);
  const data = results.map(query => query.data).filter(Boolean);

  return {
    data,
    isLoading,
    isError,
    refetch: () => Promise.all(results.map(query => query.refetch())),
  };
}